import classNames from 'classnames'
import type { Nil } from '@pbt/pbt-ui-components'

import { ConversationTransport } from '~/api/graphql/generated/types'
import EmailPreview from '~/components/common/emailPreview/EmailPreview'
import { MessageVariant, MessageWay } from '~/constants/communications'
import { useChatSenderRecipientInfo } from '~/store/hooks/conversations'
import type { ChatMessage, Recipient } from '~/types'
import {
  getMessageTransportName,
  isInternalNote,
  isPlainTextTransport,
} from '~/utils/communicationsUtils'

import MessageActions from './MessageActions'
import MessageHeader from './MessageHeader'
import MessageStatusTag from './MessageStatusTag'
import MessageTypeIcon from './MessageTypeIcon'

import styles from './MessageCardV2.module.scss'

export interface MessageCardV2Props {
  message: ChatMessage
  readOnly?: boolean
  recipient: Recipient | Nil
  variant?: MessageVariant
}
const MessageCardV2 = ({
  message,
  readOnly = false,
  variant = MessageVariant.PRIMARY,
  recipient,
}: MessageCardV2Props) => {
  const { content, type, isMedical, way, transport } = message || {}
  const isMiniMode = variant === MessageVariant.MINI
  const isInternalNoteMessage = isInternalNote(transport, type)
  const isPlainText = isPlainTextTransport(transport) && !isInternalNoteMessage
  const isInboundEmail =
    transport === ConversationTransport.Email && way === MessageWay.INBOUND
  const messageTransport = isInternalNoteMessage ? null : transport
  const iconLabel = getMessageTransportName(messageTransport)
  const { sender, isClientCommunication } = useChatSenderRecipientInfo({
    message,
    recipient,
  })
  return (
    <div className={styles.message}>
      <MessageHeader
        isClientCommunication={isClientCommunication}
        isReadOnly={readOnly}
        message={message}
        sender={sender}
      />
      <div
        className={classNames(styles.messageCard, {
          [styles.internalNoteMessage]: isInternalNoteMessage,
          [styles.medicalMessage]: isMedical,
          [styles.adminMessage]: !isMedical && !isInternalNoteMessage,
        })}
      >
        <div className={styles.messageTypeLabel}>
          <div className={styles.messageTypeIcon}>
            <MessageTypeIcon messageTransport={messageTransport} />
          </div>
          <div className={styles.messageTypeText}>{iconLabel}</div>
          <MessageStatusTag
            isInternalNote={isInternalNoteMessage}
            status={message.status}
          />
        </div>
        {content && (
          <EmailPreview
            allowPointerEvents
            classes={{
              previewContainerWrapper: classNames({
                [styles.previewContainerWrapper]: !isInboundEmail,
                [styles.previewContainerWrapperWithBackground]: isInboundEmail,
              }),
              plainTextPreview: classNames({
                [styles.primaryPlainTextPreview]: !isMiniMode,
              }),
            }}
            disableHtmlRendering={isPlainText}
            emailPreviewShadowDomClasses={{
              previewContainer: classNames({
                [styles.miniShadowDomPreviewContainer]: isMiniMode,
                [styles.primaryShadowDomPreviewContainer]: !isMiniMode,
              }),
            }}
            emailTemplate={content}
            scale={1}
          />
        )}
      </div>
      <MessageActions message={message} readOnly={readOnly} />
    </div>
  )
}

export default MessageCardV2
